import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaHome } from "react-icons/fa";
import { BsInfoCircleFill } from "react-icons/bs";

export const ContactHeroSection = () => {
  const canvasRef = useRef(null);
  const [backgroundOption, setBackgroundOption] = useState(2); // Change this number (1-4) to switch background options

  useEffect(() => {
    // Only set up canvas for option 1 - Web Animation
    if (backgroundOption === 1) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      const particles = [];
      const particleCount = 100;

      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 2 + 1,
          color: `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(
            Math.random() * 255
          )}, 255, ${Math.random() * 0.5 + 0.1})`,
          speedX: Math.random() * 2 - 1,
          speedY: Math.random() * 2 - 1,
        });
      }

      const animate = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        particles.forEach((particle) => {
          particle.x += particle.speedX;
          particle.y += particle.speedY;

          if (particle.x < 0 || particle.x > canvas.width) {
            particle.speedX = -particle.speedX;
          }

          if (particle.y < 0 || particle.y > canvas.height) {
            particle.speedY = -particle.speedY;
          }

          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
          ctx.fillStyle = particle.color;
          ctx.fill();
        });

        // Connect particles with lines if they're close enough
        particles.forEach((particle, i) => {
          particles.slice(i + 1).forEach((otherParticle) => {
            const dx = particle.x - otherParticle.x;
            const dy = particle.y - otherParticle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < 100) {
              ctx.beginPath();
              ctx.strokeStyle = `rgba(125, 125, 255, ${
                0.2 - distance / 500
              })`;
              ctx.lineWidth = 0.5;
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(otherParticle.x, otherParticle.y);
              ctx.stroke();
            }
          });
        });

        requestAnimationFrame(animate);
      };

      animate();

      const handleResize = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      };

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [backgroundOption]);

  // Animation variants for motion components
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  // Background Option 2 - Abstract Fluid Shapes
  const renderFluidBackground = () => {
    return (
      <>
        <div className="absolute inset-0 bg overflow-hidden">
          {/* Fluid SVG Shapes */}
          <svg
            className="absolute w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <path
              d="M0,50 Q30,30 50,50 T100,50 V100 H0 Z"
              fill="url(#gradient1)"
              className="animate-slow-move1"
            >
              <animate
                attributeName="d"
                dur="15s"
                repeatCount="indefinite"
                values="
                  M0,50 Q30,30 50,50 T100,50 V100 H0 Z;
                  M0,45 Q35,55 50,45 T100,45 V100 H0 Z;
                  M0,40 Q25,60 50,40 T100,40 V100 H0 Z;
                  M0,45 Q35,40 50,45 T100,45 V100 H0 Z;
                  M0,50 Q30,30 50,50 T100,50 V100 H0 Z"
              />
            </path>
            <path
              d="M0,70 Q40,60 60,70 T100,70 V100 H0 Z"
              fill="url(#gradient2)"
              opacity="0.7"
              className="animate-slow-move2"
            >
              <animate
                attributeName="d"
                dur="20s"
                repeatCount="indefinite"
                values="
                  M0,70 Q40,60 60,70 T100,70 V100 H0 Z;
                  M0,75 Q50,65 60,75 T100,75 V100 H0 Z;
                  M0,65 Q45,75 60,65 T100,65 V100 H0 Z;
                  M0,70 Q35,65 60,70 T100,70 V100 H0 Z;
                  M0,70 Q40,60 60,70 T100,70 V100 H0 Z"
              />
            </path>
            <defs>
              <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#263283" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.3" />
              </linearGradient>
            
            </defs>
          </svg>
          
          {/* Floating bubble elements */}
          <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-gradient-to-br from-blue-500/50 to-purple-500/50 blur-3xl animate-float-slow"></div>
          <div className="absolute bottom-1/3 right-1/5 w-80 h-80 rounded-full bg-gradient-to-br from-red-500/50 to-orange-500/50 blur-3xl animate-float"></div>
        </div>
        
        {/* Subtle texture overlay */}
        <div 
          className="absolute inset-0 opacity-5 mix-blend-overlay"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
          }}
        ></div>
      </>
    );
  };

  // Background Option 3 - Geometric Grid
  const renderGeometricBackground = () => {
    return (
      <>
        {/* Base dark gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#263283] to-[#ed2924]"></div>
        
        {/* Grid lines */}
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        
        {/* Perspective grid effect - diagonal lines radiating from center */}
        <div className="absolute inset-0 overflow-hidden">
          <svg className="absolute w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            {/* Create 20 lines radiating from center bottom */}
            {[...Array(20)].map((_, i) => (
              <line 
                key={i} 
                x1="50" 
                y1="100" 
                x2={i * 5} 
                y2="0" 
                stroke="rgba(255,255,255,0.1)" 
                strokeWidth="0.1"
              />
            ))}
            {[...Array(20)].map((_, i) => (
              <line 
                key={i + 20} 
                x1="50" 
                y1="100" 
                x2={100 - i * 5} 
                y2="0" 
                stroke="rgba(255,255,255,0.1)" 
                strokeWidth="0.1"
              />
            ))}
            
            {/* Horizontal lines */}
            {[...Array(10)].map((_, i) => (
              <line 
                key={i + 40} 
                x1="0" 
                y1={i * 10} 
                x2="100" 
                y2={i * 10} 
                stroke="rgba(255,255,255,0.1)" 
                strokeWidth="0.1"
              />
            ))}
          </svg>
        </div>
        
        {/* Glowing geometric shapes */}
        <div className="absolute top-1/4 left-1/4 w-32 h-32 rotate-45 bg-blue-500/5 blur-md"></div>
        <div className="absolute top-1/3 right-1/4 w-24 h-24 rotate-12 bg-purple-500/5 blur-md"></div>
        <div className="absolute bottom-1/4 left-1/3 w-40 h-40 rotate-30 bg-red-500/5 blur-md"></div>
        
        {/* Moving light effects */}
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-[#263283] to-[#ed2924]  rounded-full animate-slow-pulse"></div>
          <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-gradient-to-r from-[#263283] to-[#ed2924] rounded-full animate-slow-pulse animation-delay-2000"></div>
        </div>
      </>
    );
  };

  // Background Option 4 - Abstract Tech/Data
  const renderTechBackground = () => {
    return (
      <>
        {/* Base dark bg with subtle gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#263283] to-[#ed2924] "></div>
        
        {/* Digital circuit pattern */}
        <div className="absolute inset-0 opacity-10" 
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 100 100'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath opacity='.5' d='M96 95h4v1h-4v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9zm-1 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9z'/%3E%3Cpath d='M6 5V0H5v5H0v1h5v94h1V6h94V5H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        ></div>
        
        {/* Glowing data lines - vertical */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div 
              key={i}
              className="absolute h-full w-px bg-gradient-to-r from-[#263283] to-[#ed2924]"
              style={{
                left: `${5 + i * 5}%`,
                opacity: Math.random() * 0.5,
                animation: `data-pulse ${5 + Math.random() * 10}s infinite ${Math.random() * 5}s`
              }}
            ></div>
          ))}
          
          {/* Horizontal data lines */}
          {[...Array(10)].map((_, i) => (
            <div 
              key={i + 20}
              className="absolute w-full h-px bg-gradient-to-r from-[#263283] to-[#ed2924]"
              style={{
                top: `${10 + i * 8}%`,
                opacity: Math.random() * 0.5,
                animation: `data-pulse-horizontal ${5 + Math.random() * 10}s infinite ${Math.random() * 5}s`
              }}
            ></div>
          ))}
        </div>
        
        {/* Digital nodes/points */}
        <div className="absolute inset-0">
          {[...Array(40)].map((_, i) => (
            <div 
              key={i + 30}
              className="absolute w-1 h-1 rounded-full bg-red-400/50"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                boxShadow: '0 0 5px rgba(59, 130, 246, 0.5)',
                animation: `node-pulse ${1 + Math.random() * 4}s infinite ${Math.random() * 5}s`
              }}
            ></div>
          ))}
        </div>
        
        {/* Larger focal points */}
        <div className="absolute top-1/4 left-1/4 w-20 h-20 rounded-full bg-gradient-to-r from-[#263283] to-[#ed2924] blur-xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-24 h-24 rounded-full bg-gradient-to-r from-[#263283] to-[#ed2924] blur-xl"></div>
      </>
    );
  };

  return (
    <div className="relative pt-12 overflow-hidden bg-[#263283]">
      {/* Render chosen background option */}
      {backgroundOption === 1 && (
        <>
          {/* Original particle canvas */}
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full z-0" />
        </>
      )}
      
      {backgroundOption === 2 && renderFluidBackground()}
      {backgroundOption === 3 && renderGeometricBackground()}
      {backgroundOption === 4 && renderTechBackground()}

      {/* Content overlay */}
      <div className="relative z-10 flex flex-col items-center justify-center px-6 pt-32 pb-4">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-5xl w-full mx-auto"
        >
          {/* Main title with 3D effect */}
          <motion.div variants={itemVariants} className="relative mb-12">
            <div className="relative flex flex-col items-center p-8">
              <h1 className="text-6xl sm:text-7xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-[#6f81f6] to-[#ed2924] drop-shadow-lg">
                CONTACT US
              </h1>
              <div className="h-1 w-24 bg-gradient-to-r from-[#263283] to-[#ed2924] rounded-full my-4"></div>
              <p className="text-gray-300 text-lg max-w-2xl text-center">
              Have questions or need support? Our team is ready to assist you with anything related to Forex trading or using our platform.Discover the story behind our passion, our mission, and the
                team that makes it all happen.
              </p>
            </div>
          </motion.div>

          {/* Navigation buttons with improved design */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row pb-4 items-center justify-center gap-6"
          >
            <Link
              to="/"
              className="group relative px-8 py-4 overflow-hidden rounded-xl bg-black bg-opacity-50 backdrop-blur-sm border border-white/10 transition-all duration-300 hover:border-fuchsia-500/50 hover:shadow-lg hover:shadow-fuchsia-500/20"
            >
              <div className="absolute inset-0 w-0 bg-gradient-to-r from-[#263283] to-[#ed2924] transition-all duration-300 ease-out opacity-80 group-hover:w-full"></div>
              <div className="relative flex items-center gap-2 text-lg font-medium text-white">
                <FaHome className="text-xl" />
                <span>Home</span>
              </div>
            </Link>

            <Link
              to="/contact"
              className="group relative px-8 py-4 overflow-hidden rounded-xl bg-gradient-to-r from-[#263283] to-[#ed2924] backdrop-blur-sm border border-white/10 transition-all duration-300 hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-500/20"
            >
              <div className="absolute inset-0 w-0 bg-gradient-to-r from-[#263283] to-[#ed2924] transition-all duration-300 ease-out opacity-80 group-hover:w-full"></div>
              <div className="relative flex items-center gap-2 text-lg font-medium text-white">
                <BsInfoCircleFill className="text-xl" />
                <span>Contact Us</span>
              </div>
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Add keyframes for animations */}
      <style jsx>{`
        @keyframes data-pulse {
          0%, 100% { opacity: 0; transform: scaleY(0.8); }
          50% { opacity: 0.5; transform: scaleY(1.1); }
        }
        
        @keyframes data-pulse-horizontal {
          0%, 100% { opacity: 0; transform: scaleX(0.8); }
          50% { opacity: 0.5; transform: scaleX(1.1); }
        }
        
        @keyframes node-pulse {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.5); }
        }
        
        @keyframes slow-move1 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes slow-move2 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        
        .animate-slow-pulse {
          animation: pulse 8s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .bg-grid-pattern {
          background-image: linear-gradient(to right, rgba(255, 255, 255, 0.1) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
          background-size: 40px 40px;
        }
      `}</style>
    </div>
  );
};